import { loggedInUser } from "../sample-data/admindata";
import { toast } from "../Utills/toast";
import store from "./store";
import { showSessionTimeoutPopup, hideSessionTimeoutPopup } from "./session/sessionSlice";
import { API_BASE_URL } from './../Utills/config';
import i18n, { supportedLanguages } from '../i18n';
import { getTerminalId } from '../Utills/terminalIdentity';
import { getFoodItemErrorMessage } from './foodItemError';
import { createIdempotencyKey, isMutationMethod } from '../Utills/idempotency';
import { queueOfflineRequest, queuedResponse, setOfflineRequestProcessor } from '../Utills/offlineQueue';
import { getContinuityResponse, isContinuityRequest, rememberContinuityResponse } from '../Utills/offlineCache';

const key = 'Admin-API-Token';
const BASE_URL = API_BASE_URL;
const getOfflineOwner = () => {
    try {
        const identity = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
        return `restaurant:${identity.restaurantId || 'unknown'}:user:${identity.userId || 'unknown'}`;
    } catch (_) {
        return 'restaurant:unknown:user:unknown';
    }
};
//const BASE_URL = 'http://172.105.63.245:5000/api/' // prod
// const BASE_URL = 'https://localhost:44350/api/' // prod
//const BASE_URL =  'https://ws792k1r-44350.inc1.devtunnels.ms/api/'
const method = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    DELETE: 'DELETE'
}

// ====== TOP OF YOUR FILE (Outside fetchAPI function) ======
// Yeh global variables queue ko manage karne ke liye hain
let isRefreshing = false;
let refreshSubscribers = [];

// Baki saari concurrent APIs ko waiting list mein daalne ke liye helper
function subscribeTokenRefresh(cb) {
    refreshSubscribers.push(cb);
}

// Jaise hi naya token aaye, waiting list wali saari APIs ko naya token pass karne ke liye helper
function onTokenRefreshed(newToken) {
    refreshSubscribers.map(cb => cb(newToken));
    refreshSubscribers = [];
}

// ====== YOUR MAIN FETCH FUNCTION ======
async function fetchAPI(urlExtention, params = null, m = 'GET', requestOptions = {}) {
    const url = BASE_URL + urlExtention;
    const idempotencyKey = isMutationMethod(m)
        ? (requestOptions.idempotencyKey || createIdempotencyKey('admin'))
        : null;
    const queueRequest = async () => {
        if (!requestOptions.queueWhenOffline || !idempotencyKey) return null;
        const operation = await queueOfflineRequest({
            urlExtension: urlExtention,
            params,
            method: m,
            idempotencyKey,
            owner: getOfflineOwner(),
            label: requestOptions.offlineLabel || 'Table order',
        });
        return queuedResponse(operation);
    };
    
    // Function to generate dynamic clean parameters and headers
    const getFetchOptions = (tokenValue) => {
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tokenValue}`,
            },
            method: m
        };
        if (idempotencyKey) {
            options.headers['Idempotency-Key'] = idempotencyKey;
        }
        if (params && m !== 'GET') {
            options.body = JSON.stringify(params);
        }
        return options;
    };

    try {
        if (requestOptions.queueWhenOffline && !navigator.onLine) return await queueRequest();
        // 1. Get current JWT from storage and execute request
        let currentToken = localStorage.getItem(key);
        let response = await fetch(url, getFetchOptions(currentToken));

        // 2. Catch the 401 Unauthorized state safely
        if (response.status === 401) {
            const refreshToken = localStorage.getItem('refreshToken');

            if (!refreshToken) {
                store.dispatch(showSessionTimeoutPopup());
                return response;
            }

            /* ====================================================================
               CRITICAL FIX: QUEUE LOCK MECHANISM
               Agar pehle se koi doosri API refresh call kar rahi hai, toh yeh wali API
               apni alag call nahi karegi. Yeh chupchaap Promise queue mein wait kareki.
               ==================================================================== */
            if (isRefreshing) {
                return new Promise((resolve) => {
                    subscribeTokenRefresh((newToken) => {
                        // Jaise hi naya token milega, yeh pichli call ko naye token ke sath retry karegi
                        resolve(fetch(url, getFetchOptions(newToken)));
                    });
                });
            }

            // Lock activate karo taaki doosri API refresh call na kare
            isRefreshing = true;
            // 3. Hit the Refresh Token API (Sirf ek unique call jayegi backend par)
            const refreshResponse = await fetch(BASE_URL + 'RefreshToken', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: refreshToken })
            });

            if (refreshResponse.ok) {
                const data = await refreshResponse.json();
                
                // New tokens ko local storage mein save karo
                localStorage.setItem(key, data.token);
                localStorage.setItem('refreshToken', data.refreshToken);
                const refreshedPayload = parseJwt(data.token);
                if (refreshedPayload?.exp) {
                    localStorage.setItem('tokenExpiry', new Date(refreshedPayload.exp * 1000).toISOString());
                }

                // Lock unlock karo
                isRefreshing = false;
                
                // Waiting list mein jitni bhi APIs ruki thi, sabko naya token de kar unhe execute karo
                onTokenRefreshed(data.token);

                // Is current call ko bhi naye token ke sath retry karo
                const retriedResponse = await fetch(url, getFetchOptions(data.token));
                await rememberContinuityResponse(url, retriedResponse);
                return retriedResponse;
            } else {
                // Refresh token invalid, revoked, or the remembered session has expired.
                isRefreshing = false;
                refreshSubscribers = []; // Queue clear karo
                
                console.warn("Refresh token invalid or expired. Purging session.");
                localStorage.clear();
                store.dispatch(showSessionTimeoutPopup());
                return response; 
            }
        }

        // Final check: Agar retry ke baad bhi kisi tarah 401 reh jaye
        if (response.status === 401) {
            store.dispatch(showSessionTimeoutPopup());
        }

        await rememberContinuityResponse(url, response);
        return response;

    } catch (error) {
        if (isContinuityRequest(url, m)) {
            const cachedResponse = await getContinuityResponse(url);
            if (cachedResponse) return cachedResponse;
        }
        if (requestOptions.queueWhenOffline && (error instanceof TypeError || !navigator.onLine)) {
            return await queueRequest();
        }
        console.error("API Pipeline Exception:", error);
        throw error;
    }
}

setOfflineRequestProcessor(operation => {
    if (operation.owner !== getOfflineOwner()) {
        return Promise.resolve(new Response('Sign in with the account that created this offline order.', { status: 423 }));
    }
    return fetchAPI(operation.urlExtension, operation.params, operation.method,
        { idempotencyKey: operation.idempotencyKey, queueWhenOffline: false });
}, getOfflineOwner);

// async function fetchAPI(urlExtention, params = null, m = 'GET') {
//     const token = localStorage.getItem('key'); // Your JWT
//     const refreshToken = localStorage.getItem('refreshToken'); // Your Refresh Token
    
//     const url = BASE_URL + urlExtention;
//     const fetchParams = {
//         headers: {
//             'Content-Type': 'application/json',
//             'Authorization': `Bearer ${token}`,
//         },
//         method: m
//     };

//     if (params && m !== 'GET') {
//         fetchParams.body = JSON.stringify(params);
//     }

//     try {
//         let response = await fetch(url, fetchParams);

//         // 1. Check if token is expired (401)
//         if (response.status === 401 && refreshToken) {
//             // 2. Call the RefreshToken endpoint
//             const refreshResponse = await fetch(BASE_URL + 'RefreshToken', {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ refreshToken: refreshToken })
//             });

//             if (refreshResponse.ok) {
//                 const data = await refreshResponse.json();
                
//                 // 3. Save new tokens
//                 localStorage.setItem('key', data.token);
//                 localStorage.setItem('refreshToken', data.refreshToken);

//                 // 4. Retry the original request with the NEW token
//                 fetchParams.headers['Authorization'] = `Bearer ${data.token}`;
//                 response = await fetch(url, fetchParams);
//             } else {
//                 // Refresh failed (refresh token also expired or revoked)
//                 console.warn("response not ok Refresh token expired. Logging out.");
//                 localStorage.clear();
//                 store.dispatch(showSessionTimeoutPopup());
//                 return response; 
//             }
//         }

//         // Final check: if it's still 401 after refresh attempt
//         if (response.status === 401) {
//             store.dispatch(showSessionTimeoutPopup());
//         }

//         return response;

//     } catch (error) {
//         console.error("API call failed:", error);
//         throw error;
//     }
// }

async function logOut(param, redirect) {
  const accessToken = localStorage.getItem(key);
  const refreshToken = localStorage.getItem('refreshToken');
  try {
    const url = `${BASE_URL}${param}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken || ''}`
      },
      body: JSON.stringify({ refreshToken: refreshToken || '' })
    });

    if (!response.ok) {
      console.warn(`Server logout returned ${response.status}; completing local sign-out.`);
    }
    return response;
  } catch (error) {
    console.warn("Server logout could not be reached; completing local sign-out.", error.message);
    return null;
  } finally {
    sessionStorage.clear();
    localStorage.clear();
    if (typeof redirect === 'function') redirect();
  }
}

async function Login(u) {
   try{

     const url = `${BASE_URL}${u}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Restaurant-Selection-Token': sessionStorage.getItem('restaurantSelectionToken') || ''
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Login failed: ${errorText}`);
    }

    const result = await response.json();
    const token = result.token;
    const refreshToken = result.refreshToken;

     if (!token) throw new Error("No token returned from API");

      // Decode JWT payload
    const payload = parseJwt(token);
    if (!payload) throw new Error("Invalid token payload");


     localStorage.setItem(key, token);
     localStorage.setItem('refreshToken', refreshToken);
     sessionStorage.removeItem('restaurantSelectionToken');
    // Update loggedInUser from payload
    const loggedInUser = {
      restaurantId: payload.restaurantId,
      restaurantName: payload.restaurantName,
      restaurantAddress: payload.restaurantAddress,
      restaurantPhone: payload.restaurantPhone,
      restaurantEmail: payload.restaurantEmail,
      userId: payload.userId,
      roleId: payload.roleId,
      name: payload.name,
      userName: payload.userName,
      packageId: payload.packageId,
      preferredLanguage: payload.preferredLanguage || 'en',
    };

     localStorage.setItem('admin_token_info', JSON.stringify(loggedInUser));
    applyPreferredLanguage(loggedInUser.preferredLanguage);
    store.dispatch(hideSessionTimeoutPopup());
    const expiryTimestamp = new Date(payload.exp * 1000);
    localStorage.setItem('tokenExpiry', expiryTimestamp.toISOString());

    return 0;

   }catch (error) {
    console.error("Error during login:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function fetchLogin(u, dto) {
  try {
    const url = `${BASE_URL}${u}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(dto)
    });

    if (!response.ok) {
      const errorText = await response.text();
     // throw new Error(`Login failed: ${errorText}`);
      toast.error(`Login failed`);
    }

    const result = await response.json(); // { token: "..." }
    const token = result.token;
    const refreshToken = result.refreshToken;
    const restros = result.restros;
    const selectionToken = result.selectionToken;

    if (!token && !restros ) throw new Error("No token returned from API");
    if(token){
       // Store token in localStorage
    localStorage.setItem(key, token);
    localStorage.setItem('refreshToken', refreshToken);

    // Decode JWT payload
    const payload = parseJwt(token);
    if (!payload) throw new Error("Invalid token payload");

    // Update loggedInUser from payload
    const loggedInUser = {
      restaurantId: payload.restaurantId,
      restaurantName: payload.restaurantName,
      restaurantAddress: payload.restaurantAddress,
      restaurantPhone: payload.restaurantPhone,
      restaurantEmail: payload.restaurantEmail,
      userId: payload.userId,
      roleId: payload.roleId,
      name: payload.name,
      userName: payload.userName,
      packageId: payload.packageId,
      preferredLanguage: payload.preferredLanguage || 'en',
    };

    // Store user info and token expiry in localStorage
    localStorage.setItem('admin_token_info', JSON.stringify(loggedInUser));
    applyPreferredLanguage(loggedInUser.preferredLanguage);
    store.dispatch(hideSessionTimeoutPopup());
    const expiryTimestamp = new Date(payload.exp * 1000);
    localStorage.setItem('tokenExpiry', expiryTimestamp.toISOString());

    return 0;
    }else if(restros && selectionToken){
         sessionStorage.setItem('restaurantSelectionToken', selectionToken);
         return restros;
    }else{
       throw new Error("No value returned from API");
    }
    

  } catch (error) {
    console.error("Error during login:", error.message);
    throw error; // allow calling function to handle error
  }
}

function parseJwt(token) {
  try {
    const base64Payload = token.split('.')[1];
    const decoded = atob(base64Payload);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

async function getEmployeeDetailByContactNumber(params, contactNumber) {

  try{

    // const response = await fetchAPI(`${params}/${contactNumber}`)

     const url = `${BASE_URL}${params}/${contactNumber}`;

      const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch users: ${errorText}`);
    }

    const users = await response.json(); 
    return users;

  }catch (error) {
    console.error("Error during fetching users:", error.message);
    throw error; // allow calling function to handle error
  } 
} 

async function verifyContactNumberAPI(params, g) {

  try{
     const contactNumber = g.phoneNumber;
     const restaurantId = g.restaurantOrBranchId;
    // const response = await fetchAPI(`${params}/${contactNumber}/${restaurantId}`)
     const url = `${BASE_URL}${params}/${contactNumber}/${restaurantId}`;
        const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch Verify Contact Number: ${errorText}`);
    }

    const otp = await response.json(); 
    return otp;

  }catch (error) {
    console.error("Error during fetching Verify Contact Number:", error.message);
    throw error; // allow calling function to handle error
  } 
} 

async function verifyOtpForgotPasswordAPI(params, g) {

  try{

     //const response = await fetchAPI(`${params}/${g.phoneNumber}/${g.otpValue}`)

       const url = `${BASE_URL}${params}/${g.phoneNumber}/${g.otpValue}`;

        const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch Verify otp: ${errorText}`);
    }

    const otp = await response.json(); 
    return otp;

  }catch (error) {
    console.error("Error during fetching Verify otp:", error.message);
    throw error; // allow calling function to handle error
  } 
} 

async function forgetPasswordAPI(param, dto) {

  try{

     const response = await fetchAPI(param, dto, method.POST)

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch reset password: ${errorText}`);
    }

    const otp = await response.json(); 
    return otp;

  }catch (error) {
    console.error("Error during fetching reset password:", error.message);
    throw error; // allow calling function to handle error
  } 
} 

async function addUser(param, data) {

    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    data.restaurantOrBranchId = admin_token_info.restaurantId;

  try {
    const response = await fetchAPI(param, data, method.POST)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add user: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success(`User '${result.name}' added successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Error during add user:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function getUsers(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const response = await fetchAPI(`${params}/${restaurantId}`)
    

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch users: ${errorText}`);
    }

    const users = await response.json(); // { token: "..." }

    return users;

  } catch (error) {
    console.error("Error during fetching users:", error.message);
    throw error; // allow calling function to handle error
  }
} 

async function fetchTablesAPI(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const url = `${BASE_URL}${params}/${restaurantId}`;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch table: ${errorText}`);
    }

    const tables = await response.json(); // { token: "..." }

    return tables;

  } catch (error) {
    console.error("Error during fetching tables:", error.message);
    throw error; // allow calling function to handle error
  }
} 

async function fetchGalleryImagesAPI(params, data) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}?search=${encodeURIComponent(data)}`;
    const response = await fetchAPI(urlExtention)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch table: ${errorText}`);
    }

    const images = await response.json(); // { token: "..." }

    return images;

  } catch (error) {
    console.error("Error during fetching images:", error.message);
    throw error; // allow calling function to handle error
  }
} 

async function fetchBannersAPI(params) {
      try {
     const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
     const restaurantId = admin_token_info.restaurantId;
     const urlExtention = `${params}/${restaurantId}`;
   
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch food Items: ${errorText}`);
    }

    const banners = await response.json(); // { token: "..." }
    return banners;

  } catch (error) {
    console.error("Error during fetching food Items:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function addBannerAPI(param,data) {
  const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    data.restaurantId = admin_token_info.restaurantId;

  try {
    const response = await fetchAPI(param, data, method.POST)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add addBannerAPI: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    return result;

  } catch (error) {
    console.error("❌ Error during add addBannerAPI:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function deleteBannerAPI(params) {
  try {
    const url = `${BASE_URL}${params}`;
    
    const response = await fetchAPI(params)

    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to add user: ${errorText}`);
    }

    toast.success(`Banner deleted successfully.`);
    return response;

  } catch (error) {
    console.error("❌ Error during deleting user:", error.message);
    throw error; // rethrow to handle outside if needed
  } 
} 

async function updateBannerStatusAPI(param, payload){
  try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const request = {
      bannerId: payload.bannerId,
      restaurantId: admin_token_info.restaurantId,
      displayOnMenu: payload.displayOnMenu,
    };
    const response = await fetchAPI(param, request, method.POST)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to update banner status");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
} 

async function updateBannerAPI(param, payload){
  try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const imageChanged = payload.bannerImage !== payload.originalBannerImage;
    
    const finalPayload = {
      bannerId: payload.bannerId,
      bannerName: payload.bannerName,
      bannerImage: imageChanged ? payload.bannerImage : "",
      restaurantId: restaurantId,
    };

    const urlExtention = `${param}`;

    const response = await fetchAPI(urlExtention, finalPayload, method.POST)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to update banner");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
}

async function fetchDashboardAPI(params, d) {
      try {

    const {start, end} = d.payload;
    const today = new Date().toISOString().split("T")[0];
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const url = `${BASE_URL}/`;
    const urlExtention = `${params}/${restaurantId}/${start}/${end}`;
    const response = await fetchAPI(urlExtention)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch table: ${errorText}`);
    }

    const tables = await response.json(); // { token: "..." }

    return tables;

  } catch (error) {
    console.error("Error during fetching tables:", error.message);
    throw error; // allow calling function to handle error
  }
} 

async function fetchServiceRequestAPI(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const url = `${BASE_URL}/`;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch service request: ${errorText}`);
    }

    const tables = await response.json(); // { token: "..." }

    return tables;

  } catch (error) {
    console.error("Error during fetching service request:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function updateServiceRequestStatusAPI(param, data) {

  const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
  const restaurantId = admin_token_info.restaurantId;

  // const url = `${BASE_URL}${param}/${restaurantId}`;
  const urlExtention = `${param}/${restaurantId}`;
  try {
    const response = await fetchAPI(urlExtention, data, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ API Error:", errorText);
      throw new Error(`Failed to update status: ${errorText}`);
    }
     
    const result = await response.json();
    toast.success("Waiter request updated successfully.");
    return result;

  } catch (error) {
    console.error("❌ Network / API Error:", error.message);
    throw error;
  }
} 

 async function updateOrderStatusAPI(param, payload) {
  try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${param}/${restaurantId}`;

    const response = await fetchAPI(urlExtention, payload, method.POST)
    if (!response.ok) {
      throw new Error("Failed to update order status");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating order status:", error);
    throw error;
  }
}

 async function addTableNumberAPI(param, start, capacity=0,end = null) {
  const admin_token_info = JSON.parse(localStorage.getItem("admin_token_info") || "{}");
  const restaurantId = admin_token_info.restaurantId;
  // const url = `${BASE_URL}${param}`;
  let  payload;
  if (end === null) {
    // Single table
    payload = { 
  tableName: start,   // ✅ matches DTO
  capacity: capacity,
  restaurantOrBranchId: restaurantId,
  tableStatus: 0,
  createdDateTime: new Date().toISOString(),  // ✅ ISO string
  updatedDateTime: new Date().toISOString(),
  isActive: true
};

  } else {
    // Bulk tables
    payload = { startTableNumber: start, endTableNumber: end, capacity: capacity, restaurantOrBranchId: restaurantId} 
  };
  
  const response = await fetchAPI(param, payload, method.POST)

  if (response.status === 409) { 
    const error = await response.json(); 
    toast.error(error.message); // "Table T1 already exists for this restaurant."
    throw new Error(error.message || "Table already exists");
  }

  if (!response.ok) {
    const message = "Failed to add table(s)";
    toast.error(message);
    throw new Error(message);
  }

  const result = await response.json();
  toast.success(end === null ? "Table added successfully." : "Tables added successfully.");
  return result;
}

function applyPreferredLanguage(languageCode) {
    if (supportedLanguages.some(({ code }) => code === languageCode)) {
        i18n.changeLanguage(languageCode);
    }
}


async function updateUser(param, data) {

const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    data.restaurantOrBranchId = admin_token_info.restaurantId;
  try {
    const response = await fetchAPI(param, data, method.POST)

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update user: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success(`User updated successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Error during updating user:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function deleteUser(params) {
  try {
    const url = `${BASE_URL}${params}`;
    
    const response = await fetchAPI(params)

    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to add user: ${errorText}`);
    }

    toast.success(`User deleted successfully.`);
    return response;

  } catch (error) {
    console.error("❌ Error during deleting user:", error.message);
    throw error; // rethrow to handle outside if needed
  } 
} 

async function deleteTableAPI(params,restaurantTableId) {
  try {
    const url = `${BASE_URL}${params}/${restaurantTableId}`;
    
    const response = await fetchAPI(`${params}/${restaurantTableId}`);

    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to add table: ${errorText}`);
    }

    toast.success(`Table deleted successfully.`);
    return response;

  } catch (error) {
    console.error("❌ Error during deleting table:", error.message);
    throw error; // rethrow to handle outside if needed
  } 
}

// Safe UUID generator that works on both HTTP and HTTPS
function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function addCategory(param, data) {

    //const newGuid = crypto.randomUUID();
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    data.restaurantOrBranchId = admin_token_info.restaurantId;
    data.categoryId = generateUUID();

  try {
    const response = await fetchAPI(param, data, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add user: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success(`Category '${result.categoryName}' added successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Error during add Category:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function getCategories(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const response = await fetchAPI(`${params}/${restaurantId}`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch users: ${errorText}`);
    }

    const users = await response.json(); // { token: "..." }

    return users;

  } catch (error) {
    console.error("Error during fetching users:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function updateCategory(param, data) {

const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    data.restaurantOrBranchId = admin_token_info.restaurantId;
  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update Category: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success(`Category updated successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Error during updating Category:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function deleteCategory(params) {
  try {
    // const response = await fetchAPI(params, null, method.DELETE);
    const response = await fetchAPI(params);

    const responseText = await response.text();

    if (!response.ok) {
      toast.error(responseText);
      throw new Error(`Failed to delete Category: ${responseText}`);
    }

    toast.warning(responseText);
    return response;

  } catch (error) {
    console.error("❌ Error during deleting Category:", error.message);
    throw error;
  } 
}

async function addFoodItem(param, data) {
   const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
   const restaurantId = admin_token_info.restaurantId;
   // 1. Create a safe copy of data without mutating the argument directly
  const payload = {
    ...data,
    restaurantId: restaurantId
  };
  try {
    const response = await fetchAPI(param, payload, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(getFoodItemErrorMessage(
        errorText,
        response.status === 409
          ? "A food item with these details already exists."
          : "Food item could not be added. Please check the details and try again."
      ));
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success(`Food item '${result.foodName}' added successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Error during add Food Item:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function getFoodItems(params,categoryId) {
      try {
    // const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    // const restaurantId = '13BA2CF3-A197-4024-9040-9CAE3DE5E4DA';
     const urlExtention = `${params}/${categoryId}`;
   
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch food Items: ${errorText}`);
    }

    const users = await response.json(); // { token: "..." }

    return users;

  } catch (error) {
    console.error("Error during fetching food Items:", error.message);
    throw error; // allow calling function to handle error
  }
} 

async function fetchRestaurantDetail(param) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${param}/${restaurantId}`;
    const response = await fetchAPI(urlExtention)

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch restaurant detail: ${errorText}`);
    }

    const users = await response.json(); // { token: "..." }

    return users;

  } catch (error) {
    console.error("Error during fetching restaurant detail:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function deleteFoodItem(params) {
  try {

    // const response = await fetchAPI(params, null, method.DELETE);
    const response = await fetchAPI(params, null, method.GET);
    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to delete Food Item: ${errorText}`);
    }

    toast.success("Food item deleted successfully.");
    return response;

  } catch (error) {
    console.error("❌ Error during deleting Food Item:", error.message);
    throw error; // rethrow to handle outside if needed
  } 
} 

async function saveBillApi(param, bill, billItems, billingType, tableOtp, payments = [], actionReason = null, reservationId = null ) {

    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    bill.restaurantOrBranchId = admin_token_info.restaurantId;
    const saveBillRequest = {
      bill,
      billItems,
      billingType,
      tableOtp,
      payments,
      actionReason,
      terminalId: getTerminalId(),
      reservationId,
    };

  try {
    const response = await fetchAPI(param, saveBillRequest, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = "Unable to save the bill. Please try again.";
      try {
        const parsed = JSON.parse(errorText);
        errorMessage = parsed.message || parsed.title || errorMessage;
      } catch {
        if (response.status < 500 && errorText && errorText.length <= 300) errorMessage = errorText;
      }
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }

    // Parse JSON response (if your API returns one)
    
    return await response.json();

  } catch (error) {
    console.error("❌ Error during save Bill Item:", error.message);
    throw error; // rethrow to handle outside if needed
  }
} 

async function addReservationAPI(param, formData ) {

    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;

    formData.restaurantOrBranchId = restaurantId;
  try {
    const response = await fetchAPI(`${param}/${restaurantId}`, formData, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      let message = "Reservation could not be saved. Please try another table or time.";
      try {
        const parsed = JSON.parse(errorText);
        message = parsed.message || parsed.detail || parsed.title || message;
      } catch {
        if (errorText && errorText.length < 300) message = errorText;
      }
      throw new Error(message);
    }

    // Parse JSON response (if your API returns one)
    
    return true;

  } catch (error) {
    console.error("❌ Error during save reservation:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function fetchReservationAPI(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const url = `${BASE_URL}${params}/${restaurantId}`;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention)
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch reservation: ${errorText}`);
    }

    const tables = await response.json(); // { token: "..." }

    return tables;

  } catch (error) {
    console.error("Error during fetching reservation:", error.message);
    throw error; // allow calling function to handle error
  }
} 

async function updateReservationAPI(param, data) {
 
   const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    data.restaurantId = admin_token_info.restaurantId;
  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update reservation: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    return result;

  } catch (error) {
    console.error("❌ Error during updating reservation:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function changeReservationStatusAPI(reservationId, status, reason = "") {
  try {
    const response = await fetchAPI(`reservations/${reservationId}/status`, { status, reason }, method.POST);
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.message || body?.errors?.[""]?.[0] || "The reservation status could not be updated.");
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating reservation status:", error.message);
    throw error;
  }
}

async function getCheckedInReservationsForTableAPI(tableNumber) {
  const normalizedTableNumber = String(tableNumber || "").replace(/^T-/i, "");
  const response = await fetchAPI(`reservations/checked-in-for-table/${normalizedTableNumber}`);
  if (!response.ok) throw new Error("Eligible reservation deposits could not be loaded.");
  return response.json();
}

async function getReservationChannelSyncHistoryAPI(take = 100) {
  const response = await fetchAPI(`reservations/channel-sync/history?take=${take}`);
  if (!response.ok) throw new Error("Channel sync activity could not be loaded.");
  return response.json();
}

async function retryReservationChannelSyncAPI(syncLogId, restaurantTableId) {
  const response = await fetchAPI(`reservations/channel-sync/${syncLogId}/retry`, { restaurantTableId }, method.POST);
  if (!response.ok) { const body = await response.json().catch(() => null); throw new Error(body?.message || body?.detail || "This booking could not be retried."); }
  return response.json();
}

async function getReservationHistoryAPI(reservationId) {
  const response = await fetchAPI(`reservations/${reservationId}/history`);
  if (!response.ok) throw new Error("Reservation history could not be loaded.");
  return response.json();
}
async function getReservationRemindersAPI(reservationId) {
  const response = await fetchAPI(`reservations/${reservationId}/reminders`);
  if (!response.ok) throw new Error("Reservation reminders could not be loaded.");
  return response.json();
}
async function getReservationDepositsAPI(reservationId) {
  const response = await fetchAPI(`reservations/${reservationId}/deposits`);
  if (!response.ok) throw new Error("Reservation deposits could not be loaded.");
  return response.json();
}
async function recordReservationDepositAPI(reservationId, payload, refund = false) {
  const response = await fetchAPI(`reservations/${reservationId}/${refund ? "deposit-refund" : "deposit"}`, payload, method.POST);
  if (!response.ok) { const body = await response.json().catch(() => null); throw new Error(body?.detail || body?.message || "Deposit action could not be completed."); }
  return response.json();
}

async function waitlistAPI(path = "", payload = null, requestMethod = method.GET) {
  const response = await fetchAPI(`waitlist${path}`, payload, requestMethod);
  if (!response.ok) { const body = await response.json().catch(() => null); throw new Error(body?.message || body?.detail || "The waitlist request could not be completed."); }
  return response.json();
}

async function updateFoodItem(param, data) {

   const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const payload = {
    ...data,
    restaurantId: admin_token_info.restaurantId
  };
  try {
    const response = await fetchAPI(param, payload, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(getFoodItemErrorMessage(
        errorText,
        response.status === 409
          ? "Another food item already uses these details."
          : "Food item could not be updated. Please check the details and try again."
      ));
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success("Menu item updated successfully.");
    return result;

  } catch (error) {
    console.error("❌ Error during updating Menu Item:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function editTableAPI(param, data) {

// const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
//     data.restaurantOrBranchId = admin_token_info.restaurantId;
  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to update Table Item: ${errorText}`);
    }

    // Parse JSON response (if your API returns one)
    const result = await response.json();
    toast.success("Table details updated successfully.");
    return result;

  } catch (error) {
    console.error("❌ Error during updating Table Item:", error.message);
    throw error; // rethrow to handle outside if needed
  }
}

async function getMenuList(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch Menu List: ${errorText}`);
    }

    const menuList = await response.json(); // { token: "..." }

    return menuList;

  } catch (error) {
    console.error("Error during fetching Menu List:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function getLiveOrderList(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}`;
    // const url = `${BASE_URL}${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch Menu List: ${errorText}`);
    }

    const liveOrderList = await response.json(); // { token: "..." }

    return liveOrderList;

  } catch (error) {
    console.error("Error during fetching live order List:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function getLiveOrderByTableOtp(params, action) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}/${action.payload}`;
    // const url = `${BASE_URL}${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch live order by table otp: ${errorText}`);
    }

    const liveOrderList = await response.json(); // { token: "..." }

    return liveOrderList;

  } catch (error) {
    console.error("Error during fetching live order list by table otp:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function getOrderHistoryListByTableOtp(params,action) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}/${action.payload}`;
    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch order history List: ${errorText}`);
    }

    const liveOrderList = await response.json(); // { token: "..." }

    return liveOrderList;

  } catch (error) {
    console.error("Error during fetching live history List:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function placeOrderAPI(params, orderList, tableName, isStaff= false) {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || null);
    const restaurantId = admin_token_info?.restaurantId;
    const userId = admin_token_info?.userId || 0;
    const tableNumber = tableName.split("-");
     const urlExtention =  !isStaff ?   
   `${params}?RestaurantId=${restaurantId}&tableNumber=${tableNumber[1]}`  :
   `${params}?RestaurantId=${restaurantId}&tableNumber=${tableNumber[1]}&userId=${userId}`;
       
  try{
     const response = await fetchAPI(urlExtention, orderList, method.POST, {
       queueWhenOffline: true,
       offlineLabel: `Table ${tableName} order`,
     });
     if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`${errorText}`);
    }

    const data = await response.json();

    return data;
  }catch(error){ console.error("Error during ordering food:", error.message); throw error;}
}

async function getBill(params, data, isViewBill = 0) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || null);
    const restaurantId = admin_token_info !== null ? admin_token_info.restaurantId : ''; //admin_token_info.restaurantId;
    const urlExtention =  isViewBill != 0 ?  `${params}/${data}` : `${params}/${data}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch bill: ${errorText}`);
    }

    const bill = await response.json(); // { token: "..." }

    return bill;

  } catch (error) {
    console.error("Error during fetching bill:", error.message);
    throw error; // allow calling function to handle error
  }
}


async function getBills(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || null);
    const restaurantId = admin_token_info !== null ? admin_token_info.restaurantId : ''; //admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch bills: ${errorText}`);
    }

    const bill = await response.json(); // { token: "..." }

    return bill;

  } catch (error) {
    console.error("Error during fetching bills:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function getBillItems(params, billId) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || null);
    const restaurantId = admin_token_info !== null ? admin_token_info.restaurantId : ''; //admin_token_info.restaurantId;
    const urlExtention = `${params}/${billId}`;
    const response = await fetchAPI(urlExtention);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch bill items: ${errorText}`);
    }

    const bill = await response.json(); // { token: "..." }

    return bill;

  } catch (error) {
    console.error("Error during fetching bill items:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function getOrderHistoryList(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch Menu List: ${errorText}`);
    }

    const liveOrderList = await response.json(); // { token: "..." }

    return liveOrderList;

  } catch (error) {
    console.error("Error during fetching live order List:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function getOrderCancelledList(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}`;
    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch cancelled order list: ${errorText}`);
    }

    const liveOrderList = await response.json(); // { token: "..." }

    return liveOrderList;

  } catch (error) {
    console.error("Error during fetching live order List:", error.message);
    throw error; // allow calling function to handle error
  }
}

async function updateMenuItemAvailabilityStatus(param, data) {

  const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
  const restaurantId = admin_token_info.restaurantId;

  const url = `${BASE_URL}${param}/${restaurantId}`;
  const urlExtention = `${param}/${restaurantId}`;
  try {
    const response = await fetchAPI(urlExtention, data, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ API Error:", errorText);
      throw new Error(`Failed to update menu item: ${errorText}`);
    }
     
    const result = await response.json();
    toast.success(`Menu item status updated successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Network / API Error:", error.message);
    throw error;
  }
} 

async function changePasswordAPI(param, data) {

  const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
  const userId = admin_token_info.userId;

   const url = `${BASE_URL}${param}`;
  const urlExtention = `${param}`;
  const request = {userId: userId, confirmPassword: data.confirmPassword , newPassword : data.newPassword}
  try {
    const response = await fetchAPI(urlExtention, request, method.POST);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ API Error:", errorText);
      throw new Error(`Failed to update password: ${errorText}`);
    }
     
    const result = await response.json();
    return result;

  } catch (error) {
    console.error("❌ Network / API Error:", error.message);
    throw error;
  }
} 

async function getTaxAPI(params) {
  try {
    const admin_token_info = JSON.parse(localStorage.getItem("admin_token_info") || "{}");
    const restaurantId = admin_token_info.restaurantId;
    const response = await fetchAPI(`${params}/${restaurantId}`);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch tax: ${errorText}`);
    }

    // ✅ Safely handle empty/null body
    const text = await response.text();
    const data = text ? JSON.parse(text) : null;

    return data;
  } catch (error) {
    console.error("Error during fetching tax:", error.message);
    throw error; // allow saga/component to handle error
  }
}


 async function updateTaxAPI(param, payload) {
  try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    payload.restaurantOrBranchId = restaurantId;
    const response = await fetchAPI(param, payload, method.POST);

    if (!response.ok) {
      throw new Error("Failed to update tax");
    }
    toast.success("Tax settings updated successfully.");
    return await response.json();
  } catch (error) {
    console.error("Error updating tax:", error);
    throw error;
  }
}

 async function updateTableStatusAPI(param, payload) {
  try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    payload.restaurantOrBranchId = restaurantId;
    const response = await fetchAPI(param, payload, method.POST);

    if (!response.ok) {
      throw new Error("Failed to update tax");
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating tax:", error);
    throw error;
  }
}

async function updateRestaurantProfileAPI(param, data) {

  // const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
  // const restaurantId = admin_token_info.restaurantId;

  const url = `${BASE_URL}${param}`;
  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ API Error:", errorText);
      throw new Error(`Failed to update profile: ${errorText}`);
    }
     
    const result = await response.json();
    toast.success(`Profile updated successfully.`);
    return result;

  } catch (error) {
    console.error("❌ Network / API Error:", error.message);
    throw error;
  }
}

async function getSessionValues(params) {
      try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const url = `${BASE_URL}${params}`;
// const response1 = await fetch('https://localhost:44350/api/set', {
//       method: 'GET',
//       credentials: "include",
//       headers: { 'Accept': 'application/json' },
//     });
//     const setsession =   response1.text();
    const response = await fetchAPI(params)
    // const response = await fetch(url, {
    //   method: 'GET',
    //   credentials: "include",
    //   headers: { 'Accept': 'application/json' }
    // });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`failed to fetch cancelled order list: ${errorText}`);
    }

    const sessionValue = await response.json(); // { token: "..." }
    return sessionValue;

  } catch (error) {
    console.error("Error during fetching live order List:", error.message);
    throw error; // allow calling function to handle error
  }
}

function fetchDetails(extention, params = {}) {
    const url = `${BASE_URL}/dashboard/${extention}`
    return fetchAPI(url, params).then().catch().finally(); // todo: finish it
}

function fetchDashboard(params = {}) {
    const url = `${BASE_URL}/dashboard/`
    return fetchAPI(url, params).then().catch().finally(); // todo: finish it
}

function hasLoggedIn() {
    return localStorage.getItem(key) ? true : false;
}

function getLoggedInUser() {
    const user = localStorage.getItem('admin_token_info');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!user || !refreshToken) return null;
    return user;
}

async function updatePreferredLanguageAPI(languageCode) {
  const response = await fetchAPI('UpdatePreferredLanguage', { languageCode }, method.POST);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update preferred language: ${errorText}`);
  }
  return response.json();
}

async function fetchAddonGroupsApi(params) {
  try {
    const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
    const restaurantId = admin_token_info.restaurantId;
    const urlExtention = `${params}/${restaurantId}`;

    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch addon groups: ${errorText}`);
    }

    const addonGroups = await response.json();
    return addonGroups;
  } catch (error) {
    console.error("Error during fetching addon groups:", error.message);
    throw error;
  }
}

async function saveAddonGroupApi(param, data) {
  const admin_token_info = JSON.parse(localStorage.getItem('admin_token_info') || '{}');
  data.restaurantId = admin_token_info.restaurantId;

  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save addon group: ${errorText}`);
    }

    const result = await response.json();
    toast.success(`Add-on group '${result.groupName || data.groupName}' saved successfully.`);
    return result;
  } catch (error) {
    console.error("❌ Error during save addon group:", error.message);
    throw error;
  }
}

async function deleteAddonGroupApi(params, addonGroupId) {
  try {
    const urlExtention = `${params}/${addonGroupId}`;
    const response = await fetchAPI(urlExtention, null, method.DELETE);

    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to delete addon group: ${errorText}`);
    }

    toast.success(`Add-on group deleted successfully.`);
    return response;
  } catch (error) {
    console.error("❌ Error during deleting addon group:", error.message);
    throw error;
  }
}

// Get currently mapped addon group IDs for a category
async function getCategoryAddonMappingsAPI(params, categoryId) {
  try {
    const urlExtention = `${params}/${categoryId}`;
    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch category addon mappings: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching category addon mappings:", error.message);
    throw error;
  }
}

// Map Addon Groups to a Category
async function mapCategoryAddonsAPI(param, data) {
  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to map category addons: ${errorText}`);
    }
    toast.success("Category add-ons updated successfully.");
    return await response.json();
  } catch (error) {
    console.error("Error mapping category addons:", error.message);
    throw error;
  }
}

// Fetch dish-level addon mappings / overrides
async function getMenuItemAddonMappingsAPI(param, menuId) {
  try {
    const urlExtention = `${param}/${menuId}/AddonOverrides`;
    const response = await fetchAPI(urlExtention);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch item addon mappings: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching menu item addon mappings:", error.message);
    throw error;
  }
}

// Fetch the fully resolved add-on groups and selectable items used by ordering screens.
async function getResolvedMenuItemAddonsAPI(menuId) {
  try {
    const response = await fetchAPI(`MenuItem/${menuId}/AddonMappings`);
    if (!response.ok) {
      throw new Error("Could not load add-ons for this item.");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching resolved menu item add-ons:", error.message);
    throw error;
  }
}

// Save dish-level override (inclusions / exclusions)
async function setMenuItemAddonOverrideAPI(param, data) {
  try {
    const response = await fetchAPI(param, data, method.POST);
    if (!response.ok) {
      const errorText = await response.text();
      toast.error(errorText);
      throw new Error(`Failed to set item addon override: ${errorText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error saving menu item addon override:", error.message);
    throw error;
  }
}

export { fetchAPI, fetchDashboard, fetchLogin, Login, addUser,updateUser, getUsers,deleteUser,fetchDetails, hasLoggedIn, logOut,
  getLoggedInUser,addCategory, getCategories,updateCategory,deleteCategory,addFoodItem ,getFoodItems,deleteFoodItem,updateFoodItem,getMenuList,
  updateMenuItemAvailabilityStatus,getLiveOrderList,updateOrderStatusAPI,getOrderHistoryList,getOrderCancelledList,getSessionValues,addTableNumberAPI,
  fetchTablesAPI, deleteTableAPI,editTableAPI,fetchRestaurantDetail,updateRestaurantProfileAPI, getTaxAPI,updateTaxAPI,updateTableStatusAPI,
  getLiveOrderByTableOtp,getOrderHistoryListByTableOtp,placeOrderAPI,getBill, saveBillApi, getBills, getBillItems, addReservationAPI,
  fetchReservationAPI, updateReservationAPI, changeReservationStatusAPI, getCheckedInReservationsForTableAPI, getReservationChannelSyncHistoryAPI, retryReservationChannelSyncAPI, getReservationHistoryAPI, getReservationRemindersAPI, getReservationDepositsAPI, recordReservationDepositAPI, waitlistAPI, fetchDashboardAPI, changePasswordAPI, fetchServiceRequestAPI, updateServiceRequestStatusAPI,
  getEmployeeDetailByContactNumber,verifyContactNumberAPI, verifyOtpForgotPasswordAPI, forgetPasswordAPI, fetchBannersAPI,addBannerAPI,deleteBannerAPI,
  updateBannerStatusAPI, updateBannerAPI,fetchGalleryImagesAPI, fetchAddonGroupsApi, saveAddonGroupApi, deleteAddonGroupApi, getCategoryAddonMappingsAPI, 
  mapCategoryAddonsAPI, getMenuItemAddonMappingsAPI, getResolvedMenuItemAddonsAPI, setMenuItemAddonOverrideAPI, updatePreferredLanguageAPI}
